(() => {
  'use strict';
  if (window.ProInformaTracking) return;
  const element = document.getElementById('proinforma-tracking-config');
  if (!element) return;
  let config;
  try { config = JSON.parse(element.textContent); } catch { return; }
  if (!config || typeof config !== 'object' || Array.isArray(config)) return;
  const version = '1.1.0';
  const editor = config.design_mode === true || window.Shopify?.designMode === true;
  const theme = window.Shopify?.theme;
  const themeId = String(theme?.id || 'unknown');
  const preview = config.mode !== 'auto' || theme?.role !== 'main' || new URLSearchParams(location.search).has('preview_theme_id');
  const target = typeof config.measurement_id === 'string' ? config.measurement_id : '';
  const targetValid = /^G-[A-Z0-9]{6,20}$/.test(target);
  const pageType = /^[a-z_]{1,30}$/.test(config.page_type || '') ? config.page_type : 'page';
  const pendingKey = 'pi_contact_pending_v2_' + (preview ? themeId : 'live');
  const legacyKey = 'pi_contact_pending_v1_' + (preview ? themeId : 'live');
  let path = location.pathname;
  try { path = decodeURIComponent(decodeURIComponent(path)); } catch { path = '/account'; }
  const blockedPath = /(?:^|\/)(?:account|accounts|checkout|checkouts|orders|challenge|password)(?:\/|$)/i.test(path);
  let canonical = location.origin + '/';
  try {
    const url = new URL(config.canonical_url, location.origin);
    let candidate = decodeURIComponent(decodeURIComponent(url.pathname));
    if (url.origin === location.origin && !/(?:^|\/)(?:account|accounts|checkout|checkouts|orders|challenge|password)(?:\/|$)/i.test(candidate) && !/[@\\\u0000-\u001f]|\d{7}/.test(candidate) && !blockedPath) canonical = location.origin + url.pathname;
  } catch { }
  const history = [];
  const counts = {queued: 0, preview: 0, blocked: 0, destination_timeouts: 0};
  let recent = new WeakMap();
  let stopped = false;
  let successHandled = false;
  let leadBusy = false;
  let generation = 0;
  let leadTimer = null;
  let leadAttempts = 0;
  const callbacks = new Set();

  function consent() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  }

  function active() { return !stopped && !editor && !blockedPath && config.production_enabled === true && consent(); }
  function storageRemove(key) { try { sessionStorage.removeItem(key); return true; } catch { return false; } }
  function clearTimer() { if (leadTimer !== null) window.clearInterval(leadTimer); leadTimer = null; }
  function cancelPendingCallbacks() {
    for (const id of callbacks) window.clearTimeout(id);
    callbacks.clear();
    leadBusy = false;
  }

  function send(name, parameters, before = () => true, after = () => {}, rollback = () => {}, settled = () => {}) {
    if (!active()) { counts.blocked++; return false; }
    const safe = {...parameters, tracking_source: 'proinforma_theme', page_type: pageType, page_location: canonical, page_referrer: '', page_title: 'ProInforma'};
    const dispatch = () => {
      if (!active() || before() !== true) { counts.blocked++; return false; }
      try {
        if (preview) {
          history.push({event: name, state: 'preview_only_not_sent', parameters: {...safe}});
          if (history.length > 30) history.shift();
          counts.preview++;
        } else {
          if (typeof window.gtag !== 'function') throw new Error('Tag unavailable');
          window.gtag('event', name, {...safe, send_to: target});
          counts.queued++;
        }
        after();
        return true;
      } catch { rollback(); counts.blocked++; return false; }
    };
    if (preview) return dispatch();
    if (!targetValid || typeof window.gtag !== 'function') { counts.blocked++; return false; }
    const epoch = generation;
    let done = false;
    const timeout = window.setTimeout(() => {
      callbacks.delete(timeout);
      if (done) return;
      done = true;
      settled();
      counts.destination_timeouts++;
    }, 2000);
    callbacks.add(timeout);
    try {
      window.gtag('get', target, 'client_id', value => {
        if (done) return;
        done = true;
        window.clearTimeout(timeout);
        callbacks.delete(timeout);
        if (epoch !== generation || !active() || typeof value !== 'string' || value.length === 0) {
          counts.blocked++;
          settled();
          return;
        }
        dispatch();
        settled();
      });
      return true;
    } catch {
      done = true;
      window.clearTimeout(timeout);
      callbacks.delete(timeout);
      counts.blocked++;
      settled();
      return false;
    }
  }

  function area(link) {
    if (link.closest('footer')) return 'footer';
    if (link.closest('header, .header-wrapper, .announcement-bar-section')) return 'header';
    return 'content';
  }

  function click(event) {
    if (event.isTrusted !== true || event.defaultPrevented || (event.button !== undefined && event.button !== 0) || !active()) return;
    const link = event.target?.closest?.('a[href]');
    if (!link) return;
    const raw = link.getAttribute('href') || '';
    let name = '';
    const parameters = {link_area: area(link)};
    if (/^tel:/i.test(raw)) { name = 'pi_contact_click'; parameters.contact_method = 'phone'; }
    else if (/^mailto:/i.test(raw)) { name = 'pi_contact_click'; parameters.contact_method = 'email'; }
    else {
      let url;
      try { url = new URL(raw, location.href); } catch { return; }
      if (!/^https?:$/.test(url.protocol)) return;
      if (['wa.me', 'api.whatsapp.com', 'web.whatsapp.com'].includes(url.hostname)) {
        name = 'pi_contact_click'; parameters.contact_method = 'whatsapp';
      } else {
        const extension = url.pathname.match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toLowerCase();
        if (!['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx', 'zip'].includes(extension)) return;
        name = 'pi_download_click'; parameters.file_extension = extension;
      }
    }
    const now = Date.now();
    if (recent.has(link) && now - recent.get(link) < 800) return;
    if (send(name, parameters)) recent.set(link, now);
  }

  function submit(event) {
    if (event.isTrusted !== true || event.defaultPrevented || !active()) return;
    const form = event.target;
    if (form?.id !== 'ContactForm' || form.tagName !== 'FORM') return;
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
    try {
      sessionStorage.setItem(pendingKey, JSON.stringify({created: Date.now(), page: canonical}));
    } catch { }
  }

  function confirmedLead() {
    if (!active() || successHandled || leadBusy) return;
    if (document.querySelector('#ContactForm [aria-invalid="true"]')) {
      storageRemove(pendingKey);
      clearTimer();
      return;
    }
    if (!document.querySelector('#ContactForm > .form-status.form-status-list.form__message')) return;
    let raw, pending;
    try { raw = sessionStorage.getItem(pendingKey); pending = JSON.parse(raw || 'null'); } catch { return; }
    if (!pending || !Number.isFinite(pending.created) || pending.page !== canonical || Date.now() < pending.created || Date.now() - pending.created > 1800000) {
      if (raw !== null) storageRemove(pendingKey);
      clearTimer();
      return;
    }
    leadBusy = true;
    const accepted = send('generate_lead', {lead_type: 'contact_form', form_id: 'ContactForm'}, () => {
      try {
        if (sessionStorage.getItem(pendingKey) !== raw) return false;
        sessionStorage.removeItem(pendingKey);
        return sessionStorage.getItem(pendingKey) === null;
      } catch { return false; }
    }, () => {
      successHandled = true;
      leadBusy = false;
      clearTimer();
    }, () => {
      leadBusy = false;
      if (consent()) { try { sessionStorage.setItem(pendingKey, raw); } catch { } }
    }, () => { leadBusy = false; });
    if (!accepted) leadBusy = false;
  }

  function resume() {
    if (!active()) return;
    storageRemove(legacyKey);
    confirmedLead();
    if (!successHandled && leadTimer === null && document.querySelector('#ContactForm > .form-status.form-status-list.form__message')) {
      leadAttempts = 0;
      leadTimer = window.setInterval(() => {
        confirmedLead();
        if (++leadAttempts >= 20 || stopped || successHandled || !active()) clearTimer();
      }, 500);
    }
  }

  function consentChanged() {
    generation++;
    cancelPendingCallbacks();
    clearTimer();
    if (!consent()) {
      recent = new WeakMap();
      history.length = 0;
      storageRemove(pendingKey);
      storageRemove(legacyKey);
      return;
    }
    resume();
  }

  function stop() {
    stopped = true;
    generation++;
    clearTimer();
    cancelPendingCallbacks();
    document.removeEventListener('click', click);
    document.removeEventListener('submit', submit);
    document.removeEventListener('visitorConsentCollected', consentChanged);
    recent = new WeakMap();
    history.length = 0;
    storageRemove(pendingKey);
  }

  window.ProInformaTracking = Object.freeze({
    status: () => ({version, theme_id: themeId, mode: editor ? 'editor' : preview ? 'preview' : 'live', analytics_allowed: consent(), measurement_configured: targetValid, google_tag_available: typeof window.gtag === 'function', production_enabled: config.production_enabled === true, blocked_path: blockedPath, stopped, counts: {...counts}}),
    diagnostics: () => history.map(item => ({...item, parameters: {...item.parameters}})),
    stop
  });
  if (editor || blockedPath || config.production_enabled !== true) return;
  document.addEventListener('click', click);
  document.addEventListener('submit', submit);
  document.addEventListener('visitorConsentCollected', consentChanged);
  resume();
})();
